'use client';

import { useLocale, useTranslations } from 'next-intl';
import { MouseEventHandler, useEffect, useState } from 'react';

import GAScript, { pushGAEvent } from '@components/Analytics/Google/GAScript';
import BannerImage from '@components/BannerImage';
import { Banner } from '@utils/api/server/wr';
import { getImageLink } from '@utils/getters';
import { useUserStore } from '@utils/user';

export type BannersPostTopProps = {
  target: string;
  banners: Banner[];
};

const BannersPostTop = ({ target, banners }: BannersPostTopProps) => {
  const lang = useLocale();
  const t = useTranslations('main.label');
  const userStore = useUserStore();
  const numberOfBanners = banners.length;

  if (numberOfBanners === 0) return null;

  const advertisementLabel = t('advertisement').toUpperCase();

  const getBanner = () => {
    const index = new Date().getMinutes() % numberOfBanners;
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const analyticsCustomParams = {
      locale: lang,
      transinfo_view_category: windowWidth > 768 ? 'external-banner-desktop' : 'external-banner-mobile',
      transinfo_url: banners[index].url,
      transinfo_title: banners[index].description,
      transinfo_extension_1: banners[index].imgSrcDesktop,
      transinfo_extension_2: banners[index].imgSrcMobile,
      transinfo_extension_3: windowWidth,
    };
    return { banner: banners[index], analyticsCustomParams };
  };

  let { banner, analyticsCustomParams } = getBanner();

  useEffect(() => {
    ({ banner, analyticsCustomParams } = getBanner());
  }, []);

  const [hidden, setHidden] = useState(false);

  const onClickHandler: MouseEventHandler<HTMLAnchorElement> = () => {
    pushGAEvent(userStore, 'transinfo_banner_ptop_click', lang, analyticsCustomParams);
  };

  if (hidden) return null;

  return (
    <aside className="w-full px-3 lg:px-4">
      <div className="leading-xs mb-2 w-full text-left text-xs font-normal">{advertisementLabel}</div>
      <div className="hidden md:block">
        <a href={banner.url} target={target} rel="sponsored nofollow" onClick={onClickHandler}>
          <BannerImage src={getImageLink(banner.imgSrcDesktop)} className="w-full rounded-md" fallbackAspectRatio="871 / 200" onError={() => setHidden(true)} />
        </a>
      </div>
      <div className="block md:hidden">
        <a href={banner.url} target={target} rel="sponsored nofollow" onClick={onClickHandler}>
          <BannerImage src={getImageLink(banner.imgSrcMobile)} className="w-full rounded-md" fallbackAspectRatio="423 / 271" onError={() => setHidden(true)} />
        </a>
      </div>
      <GAScript event="transinfo_banner_ptop_view" id={banner.id} values={analyticsCustomParams} />
    </aside>
  );
};
export default BannersPostTop;
